import { useParams, useNavigate } from 'react-router-dom';
import {
  getDistrict,
  createDistrict,
  updateDistrict,
  updateDistrictAccount,
  getDistrictContacts,
  createDistrictContact,
  updateDistrictContact,
  deleteDistrictContact,
  updateDistrictAccountAdmin,
  getDistrictSubscriptions,
  addDistrictSubscription,
  updateDistrictSubscription,
  deleteDistrictSubscription,
  getDistrictSchools,
  linkDistrictSchool,
  unlinkDistrictSchool,
} from '../api/districts-api';
import { listProducts } from '../api/products-api';
import { listAdvocates } from '../api/advocates-api';
import { useAsync } from '../hooks/useAsync';
import { StatusBadge, LoadingBlock, ErrorBanner } from '../components/ui';
import { EntityForm, fieldsToReadItems, type FieldDef } from '../components/EntityForm';
import { DetailSection, EditableSection, DefinitionList, type ReadItem } from '../components/DetailSection';
import { ContactsTab } from '../components/ContactsTab';
import { SubscriptionsTab } from '../components/SubscriptionsTab';
import { SchoolsTab } from '../components/SchoolsTab';
import { AccountPanel } from '../components/AccountPanel';
import { CreateEntity } from '../components/CreateEntity';
import { Button } from '../neon/controls';
import { contactName } from '../utils/format';
import { getSiblings } from '../utils/browse-context';
import { NeonBreadcrumbs } from '@ps-refarch-ux/neon';

const SCHOOL_TYPE_OPTIONS = [
  { value: 0, label: 'Public' },
  { value: 1, label: 'Private' },
];

const INFO_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Name' },
  { key: 'school_type', label: 'Type', type: 'select', options: SCHOOL_TYPE_OPTIONS },
  { key: 'address_line1', label: 'Address 1' },
  { key: 'address_line2', label: 'Address 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP' },
  { key: 'country', label: 'Country' },
  { key: 'phone', label: 'Phone' },
  { key: 'fax', label: 'Fax' },
  { key: 'url', label: 'Website' },
  { key: 'enrollment', label: 'Enrollment', type: 'number' },
  { key: 'nces_district_id', label: 'NCES district ID' },
  { key: 'adoption_stage', label: 'Adoption stage (code)', type: 'number' },
  { key: 'is_enterprise', label: 'Enterprise', type: 'checkbox' },
];

const CREATE_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Name' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'country', label: 'Country' },
];

export function DistrictDetailPage() {
  const { ndid = '' } = useParams();
  const navigate = useNavigate();

  if (ndid === 'new') {
    return (
      <CreateEntity
        title="New district"
        fields={CREATE_FIELDS}
        create={async (body) => {
          const { district } = await createDistrict(body);
          return district.ndid;
        }}
        onCreated={(id) => navigate(`/districts/${encodeURIComponent(id)}`)}
        backTo="/districts"
      />
    );
  }

  return <DistrictDetail ndid={ndid} navigateBack={() => navigate('/districts')} />;
}

function DistrictDetail({ ndid, navigateBack }: { ndid: string; navigateBack: () => void }) {
  const navigate = useNavigate();
  const { prev, next } = getSiblings('districts', ndid);
  const district = useAsync(() => getDistrict(ndid), [ndid]);
  const contacts = useAsync(() => getDistrictContacts(ndid), [ndid]);
  const subscriptions = useAsync(() => getDistrictSubscriptions(ndid), [ndid]);
  const schools = useAsync(() => getDistrictSchools(ndid), [ndid]);
  const products = useAsync(() => listProducts(), []);
  const advocates = useAsync(() => listAdvocates(), []);

  if (district.loading && !district.data) return <LoadingBlock label="Loading district…" />;
  if (district.error) return <ErrorBanner error={district.error} />;
  const detail = district.data?.district;
  if (!detail) return <ErrorBanner error="District not found." />;

  const contactList = contacts.data?.contacts ?? [];

  const contactOptions = contactList.map((c) => ({ value: c.id, label: `${contactName(c)} (#${c.id})` }));
  const contactById = (id: any) => {
    const c = contactList.find((x) => String(x.id) === String(id));
    return c ? `${contactName(c)} (#${c.id})` : '—';
  };
  const adminFields: FieldDef[] = [
    { key: 'primary_contact_id', label: 'Primary contact', type: 'select', options: contactOptions, allowEmpty: true, emptyText: '— none —' },
    { key: 'technical_contact_id', label: 'Technical contact', type: 'select', options: contactOptions, allowEmpty: true, emptyText: '— none —' },
    { key: 'sales_contact_id', label: 'Sales contact', type: 'select', options: contactOptions, allowEmpty: true, emptyText: '— none —' },
    { key: 'billing_contact_id', label: 'Billing contact', type: 'select', options: contactOptions, allowEmpty: true, emptyText: '— none —' },
  ];
  const idItems: ReadItem[] = [
    { label: 'Naviance District ID', value: detail.ndid },
    { label: 'Institution UUID', value: detail.institution_uuid || '—' },
    { label: 'NCES district ID', value: detail.nces_district_id || '—' },
    {
      label: 'Website',
      value: detail.url ? (
        <a
          href={/^https?:\/\//.test(detail.url) ? detail.url : `https://${detail.url}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {detail.url}
        </a>
      ) : (
        '—'
      ),
    },
  ];
  const adminReadItems: ReadItem[] = [
    { label: 'Primary contact', value: contactById(detail.primary_contact_id) },
    { label: 'Technical contact', value: contactById(detail.technical_contact_id) },
    { label: 'Sales contact', value: contactById(detail.sales_contact_id) },
    { label: 'Billing contact', value: contactById(detail.billing_contact_id) },
  ];

  return (
    <div className="page">
      <div className="detail-header">
        <div>
          <NeonBreadcrumbs
            id="dist-breadcrumbs"
            breadcrumbs={[
              {
                text: 'Districts',
                url: '/districts',
                onUserClick: (e: Event) => {
                  e.preventDefault();
                  navigate('/districts');
                },
              },
              { text: detail.name, url: '#' },
            ]}
          />
          <h2>
            {detail.name} <StatusBadge status={detail.accountStatus} />
          </h2>
          <div className="subtle">
            {detail.ndid} · {detail.city}, {detail.state}
          </div>
        </div>
        <div className="detail-actions">
          {prev ? (
            <Button id="dist-prev" text="Previous" variant="tertiary" icon="chevron-left" onClick={() => navigate(`/districts/${encodeURIComponent(prev)}`)} />
          ) : null}
          {next ? (
            <Button id="dist-next" text="Next" variant="tertiary" icon="chevron-right" iconPosition="right" onClick={() => navigate(`/districts/${encodeURIComponent(next)}`)} />
          ) : null}
          <Button id="dist-back" text="Back" variant="tertiary" icon="arrow-left" onClick={navigateBack} />
        </div>
      </div>

      <div className="detail-sections">
        <DetailSection title="Summary">
          <DefinitionList items={idItems} />
        </DetailSection>

        <EditableSection
          title="District Overview"
          read={<DefinitionList items={fieldsToReadItems(INFO_FIELDS, detail)} />}
          renderEdit={(done) => (
            <EntityForm
              fields={INFO_FIELDS}
              initial={detail}
              save={(patch) => updateDistrict(ndid, patch)}
              onSaved={() => {
                district.reload();
                done();
              }}
              onCancel={done}
            />
          )}
        />

        <AccountPanel
          idPrefix="dist"
          detail={detail}
          advocates={advocates.data?.advocates ?? []}
          onUpdateAccount={(patch) => updateDistrictAccount(ndid, patch)}
          onChanged={() => district.reload()}
        />

        <SubscriptionsTab
          subscriptions={subscriptions.data?.subscriptions ?? []}
          products={products.data?.products ?? []}
          loading={subscriptions.loading}
          onReload={subscriptions.reload}
          add={(body) => addDistrictSubscription(ndid, body)}
          update={(pid, body) => updateDistrictSubscription(ndid, pid, body)}
          remove={(pid) => deleteDistrictSubscription(ndid, pid)}
          showEnrollment={false}
        />

        <SchoolsTab
          schools={schools.data?.schools ?? []}
          loading={schools.loading}
          onReload={schools.reload}
          link={(nid) => linkDistrictSchool(ndid, nid)}
          unlink={(nid) => unlinkDistrictSchool(ndid, nid)}
        />

        <ContactsTab
          contacts={contactList}
          loading={contacts.loading}
          onReload={contacts.reload}
          create={(body) => createDistrictContact(ndid, body)}
          update={(id, body) => updateDistrictContact(ndid, id, body)}
          remove={(id) => deleteDistrictContact(ndid, id)}
        />

        <EditableSection
          title="Key Contacts"
          read={<DefinitionList items={adminReadItems} />}
          renderEdit={(done) => (
            <EntityForm
              fields={adminFields}
              initial={detail}
              save={(patch) => updateDistrictAccountAdmin(ndid, patch)}
              submitLabel="Save key contacts"
              onSaved={() => {
                district.reload();
                done();
              }}
              onCancel={done}
            />
          )}
        />
      </div>
    </div>
  );
}
